import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import NavbarAuth from '../components/NavbarAuth';
import api from '../services/api';

// ═══ Hook responsive ═══
const useResponsiveLocal = () => {
    const getState = () => {
        const w = window.innerWidth;
        return { isSmall: w <= 480, isMobile: w <= 768, isTablet: w > 768 && w <= 968 };
    };
    const [state, setState] = useState(getState);
    useEffect(() => {
        const handleResize = () => setState(getState());
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);
    return state;
};

const GOLD = '#c9a84c';
const Icon = ({ path, size = 18, color = GOLD }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>{path}</svg>
);

const icons = {
    check: <polyline points="20 6 9 17 4 12" />,
    crown: <path d="M2 18h20l-2-9-5 4-3-6-3 6-5-4-2 9z" />,
    calendar: <><rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></>,
    users: <><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></>,
    receipt: <><path d="M4 2v20l2-1 2 1 2-1 2 1 2-1 2 1 2-1 2 1V2l-2 1-2-1-2 1-2-1-2 1-2-1-2 1z" /><line x1="8" y1="8" x2="16" y2="8" /><line x1="8" y1="12" x2="16" y2="12" /></>,
    arrowRight: <><line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" /></>,
};

const AbonnementConfirmation = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const { isSmall, isMobile } = useResponsiveLocal();

    const [data, setData] = useState(null);
    const [plans, setPlans] = useState([]);
    const [loading, setLoading] = useState(true);
    const [myPhoto, setMyPhoto] = useState(null);

    useEffect(() => {
        fetchData();
        if (user?.id) fetchMyProfile();
    }, [user]);

    const fetchData = async () => {
        try {
            const [abo, pl] = await Promise.all([
                api.get('/abonnements/mon-abonnement'),
                api.get('/abonnements/plans'),
            ]);
            setData(abo.data);
            setPlans(pl.data.plans);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const fetchMyProfile = async () => {
        if (!user?.id) return;
        try {
            const response = await api.get(`/profiles/${user.id}`);
            if (response.data) setMyPhoto(response.data.photo_url);
        } catch (err) {
            console.error(err);
        }
    };

    const formatDate = (d) => new Date(d).toLocaleDateString('fr-FR', {
        day: 'numeric', month: 'long', year: 'numeric'
    });

    const formatPrix = (prix, devise) => {
        const n = parseFloat(prix);
        if (n === 0) return 'Gratuit';
        return `${n.toFixed(2).replace('.', ',')} ${devise === 'EUR' ? '€' : devise}`;
    };

    // Nombre de jours restants
    const joursRestants = (dateFin) => {
        const diff = new Date(dateFin) - new Date();
        return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
    };

    const abo = data?.abonnement;
    const libellePlan = abo ? (plans.find(p => p.code === abo.code)?.libelle || abo.code) : '';
    const dernierPaiement = data?.paiements?.[0] || null;

    const layoutStyle = {
        maxWidth: '820px',
        margin: '0 auto',
        padding: isSmall ? '12px' : isMobile ? '16px' : '48px 32px',
        boxSizing: 'border-box',
    };

    return (
        <div style={styles.page}>
            <NavbarAuth myPhoto={myPhoto} />

            <div style={layoutStyle}>
                <main style={{ ...styles.mainCard, padding: isSmall ? '20px' : isMobile ? '26px' : '44px' }}>

                    {loading ? (
                        <div style={styles.loading}>Chargement...</div>
                    ) : !abo ? (
                        // ═══ Aucun abonnement trouvé ═══
                        <div style={{ textAlign: 'center' }}>
                            <h2 style={styles.title}>Aucun abonnement actif</h2>
                            <p style={styles.subtitle}>
                                Nous n'avons trouvé aucun abonnement en cours sur votre compte.
                            </p>
                            <button onClick={() => navigate('/abonnement')} style={styles.mainBtn}>
                                Voir les formules <Icon path={icons.arrowRight} size={16} color="#f3e0c7" />
                            </button>
                        </div>
                    ) : (
                        <>
                            {/* ═══ En-tête de succès ═══ */}
                            <div style={styles.successHead}>
                                <div style={styles.successCircle}>
                                    <Icon path={icons.check} size={38} color="#fff" />
                                </div>
                                <h2 style={{ ...styles.title, fontSize: isSmall ? '24px' : '30px' }}>
                                    Merci, votre abonnement est actif
                                </h2>
                                <p style={styles.subtitle}>
                                    Vous avez désormais accès à l'ensemble des fonctionnalités
                                    de Zawaj &amp; Tawadud. Qu'Allah facilite votre recherche.
                                </p>
                            </div>

                            {/* ═══ Récapitulatif ═══ */}
                            <div style={styles.recapCard}>
                                <h3 style={styles.recapTitle}>
                                    <Icon path={icons.crown} size={17} color="#c9a84c" /> Votre formule
                                </h3>

                                <div style={styles.recapLigne}>
                                    <span style={styles.recapLabel}>Formule souscrite</span>
                                    <span style={styles.recapValBig}>{libellePlan}</span>
                                </div>
                                <div style={styles.recapLigne}>
                                    <span style={styles.recapLabel}>Durée</span>
                                    <span style={styles.recapVal}>{abo.duree_mois} mois</span>
                                </div>
                                <div style={styles.recapLigne}>
                                    <span style={styles.recapLabel}>Montant réglé</span>
                                    <span style={styles.recapVal}>{formatPrix(abo.prix_paye, abo.devise)}</span>
                                </div>
                                <div style={styles.recapLigne}>
                                    <span style={styles.recapLabel}>Date de souscription</span>
                                    <span style={styles.recapVal}>{formatDate(abo.date_debut)}</span>
                                </div>
                                <div style={{ ...styles.recapLigne, borderBottom: 'none' }}>
                                    <span style={styles.recapLabel}>Expire le</span>
                                    <span style={{ ...styles.recapValBig, color: '#2c5f2e' }}>
                                        {formatDate(abo.date_fin)}
                                    </span>
                                </div>

                                <div style={styles.joursBox}>
                                    <Icon path={icons.calendar} size={15} color="#2c5f2e" />
                                    <span>
                                        Il vous reste <strong>{joursRestants(abo.date_fin)} jours</strong> d'accès
                                    </span>
                                </div>
                            </div>

                            {/* ═══ Reçu ═══ */}
                            {dernierPaiement && (
                                <div style={styles.recuCard}>
                                    <h3 style={styles.recapTitle}>
                                        <Icon path={icons.receipt} size={17} color="#c9a84c" /> Justificatif de paiement
                                    </h3>
                                    <div style={styles.recapLigne}>
                                        <span style={styles.recapLabel}>Référence</span>
                                        <span style={styles.recapValMono}>#{String(dernierPaiement.id).padStart(6, '0')}</span>
                                    </div>
                                    <div style={styles.recapLigne}>
                                        <span style={styles.recapLabel}>Moyen de paiement</span>
                                        <span style={styles.recapVal}>
                                            {dernierPaiement.methode === 'paypal' ? 'PayPal'
                                                : dernierPaiement.methode === 'carte' ? 'Carte bancaire'
                                                    : 'Mode test'}
                                        </span>
                                    </div>
                                    <div style={{ ...styles.recapLigne, borderBottom: 'none' }}>
                                        <span style={styles.recapLabel}>Date</span>
                                        <span style={styles.recapVal}>{formatDate(dernierPaiement.created_at)}</span>
                                    </div>
                                </div>
                            )}

                            {/* ═══ Actions ═══ */}
                            <div style={{ ...styles.actions, flexDirection: isSmall ? 'column' : 'row' }}>
                                <button onClick={() => navigate('/dashboard')} style={styles.mainBtn}>
                                    <Icon path={icons.users} size={16} color="#f3e0c7" />
                                    Accéder à l'annuaire
                                </button>
                                <button onClick={() => navigate('/abonnement')} style={styles.secondBtn}>
                                    Gérer mon abonnement
                                </button>
                            </div>

                            {/* ═══ Historique ═══ */}
                            {data.paiements && data.paiements.length > 1 && (
                                <div style={styles.historiqueBox}>
                                    <h4 style={styles.historiqueTitle}>Historique de vos paiements</h4>
                                    {data.paiements.map(p => (
                                        <div key={p.id} style={styles.historiqueLigne}>
                                            <span style={styles.historiqueDate}>{formatDate(p.created_at)}</span>
                                            <span style={styles.historiqueCode}>
                                                {plans.find(pl => pl.code === p.code)?.libelle || p.code}
                                            </span>
                                            <span style={styles.historiqueMontant}>{formatPrix(p.montant, p.devise)}</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </>
                    )}
                </main>
            </div>
        </div>
    );
};

const styles = {
    page: { minHeight: '100vh', width: '100%', boxSizing: 'border-box', overflowX: 'hidden', background: 'linear-gradient(135deg, #3d0a26 0%, #5c0f33 50%, #2a0518 100%)', backgroundAttachment: 'fixed', fontFamily: 'Arial, sans-serif' },
    mainCard: { background: 'linear-gradient(135deg, #fdf2f5, #faeef2)', borderRadius: '20px', boxShadow: '0 10px 40px rgba(0,0,0,0.25)', minWidth: 0 },

    successHead: { textAlign: 'center', marginBottom: '32px' },
    successCircle: { width: '76px', height: '76px', borderRadius: '50%', background: 'linear-gradient(135deg, #2c5f2e, #3d8b40)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px', boxShadow: '0 6px 20px rgba(44,95,46,0.3)' },
    title: { color: '#4a0028', fontWeight: '800', margin: '0 0 12px', fontFamily: 'Cormorant Garamond, serif' },
    subtitle: { color: '#9a7d88', fontSize: '14px', lineHeight: '1.7', margin: 0 },

    recapCard: { background: '#fff', borderRadius: '16px', padding: '24px 26px', border: '1px solid #ecd9b9', boxShadow: '0 4px 16px rgba(74,0,40,0.06)', marginBottom: '18px' },
    recuCard: { background: 'rgba(201,168,76,0.06)', borderRadius: '16px', padding: '24px 26px', border: '1px solid #ecd9b9', marginBottom: '24px' },
    recapTitle: { display: 'flex', alignItems: 'center', gap: '8px', color: '#7a1340', fontSize: '15px', fontWeight: '800', margin: '0 0 18px' },
    recapLigne: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', padding: '11px 0', borderBottom: '1px solid #f0e4e9' },
    recapLabel: { color: '#9a7d88', fontSize: '13px', fontWeight: '600' },
    recapVal: { color: '#4a0028', fontSize: '13.5px', fontWeight: '700', textAlign: 'right' },
    recapValBig: { color: '#4a0028', fontSize: '16px', fontWeight: '800', textAlign: 'right' },
    recapValMono: { color: '#4a0028', fontSize: '13px', fontWeight: '700', fontFamily: 'monospace', textAlign: 'right' },

    joursBox: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', marginTop: '18px', padding: '12px', background: 'rgba(44,95,46,0.08)', borderRadius: '10px', color: '#2c5f2e', fontSize: '13px', fontWeight: '600' },

    actions: { display: 'flex', gap: '14px', marginBottom: '24px' },
    mainBtn: { flex: 1, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '8px', padding: '14px 24px', background: 'linear-gradient(135deg, #4a0028, #8b1a4a)', color: '#f3e0c7', border: 'none', borderRadius: '12px', cursor: 'pointer', fontWeight: '700', fontSize: '14.5px', boxShadow: '0 4px 16px rgba(74,0,40,0.3)' },
    secondBtn: { flex: 1, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '8px', padding: '14px 24px', backgroundColor: '#fff', color: '#8b1a4a', border: '1.5px solid #e6cfa8', borderRadius: '12px', cursor: 'pointer', fontWeight: '700', fontSize: '14.5px' },

    historiqueBox: { borderTop: '1px solid #f0d9e3', paddingTop: '20px' },
    historiqueTitle: { color: '#7a1340', fontSize: '14px', fontWeight: '800', margin: '0 0 14px' },
    historiqueLigne: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', padding: '9px 0', borderBottom: '1px solid #f7ecf1', fontSize: '12.5px' },
    historiqueDate: { color: '#9a7d88', flex: 1 },
    historiqueCode: { color: '#4a0028', fontWeight: '600', flex: 1, textAlign: 'center' },
    historiqueMontant: { color: '#4a0028', fontWeight: '800', flex: 1, textAlign: 'right' },

    loading: { textAlign: 'center', color: '#4a0028', fontSize: '16px', fontWeight: '600', padding: '40px 0' },
};

export default AbonnementConfirmation;